/** Popover: provider tabs, model list (with search on OpenCode tab), think-effort segmented control. */
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import type en from "@/i18n/messages/input-bar-pickers/en";
import { Button, FormError, Icons, TextInput } from "@/components/ui";
import { CliLogo } from "@/components/ui/icons";
import { useSummonedInputMenuStyle } from "@/components/ui/summoned-input-surface-context";
import {
  menuItemVariants,
  menuSurfaceVariants,
} from "@/components/ui/dropdown-menu";
import {
  getModelEffortConfig,
  getModelsForRuntime,
  usesOpencodePlanGroups,
  type EffortChoice,
  type RuntimeKey,
} from "@/lib/model-registry";
import { providerRuntimeId } from "@/lib/denker-composer-agent";
import { useCompanionStore } from "@/stores/companion-store";
import type { InputBarProvider } from "./input-bar-model-pill";
import { ModelPickerList } from "./model-picker-list";
import {
  MODEL_PICKER_CONTROL_HEIGHT,
  MODEL_PICKER_CONTROL_SHAPE,
  PROVIDER_LABELS,
  providerGridClass,
  setForwardedRef,
} from "./model-picker-popover-helpers";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";

type Effort = EffortChoice;

const EFFORT_LABEL_KEYS = {
  default: "effortDefault",
  minimal: "effortMinimal",
  low: "effortLow",
  medium: "effortMedium",
  high: "effortHigh",
  xhigh: "effortXhigh",
  max: "effortMax",
  ultra: "effortUltra",
} as const satisfies Record<Effort, keyof typeof en>;

interface Props {
  providers: InputBarProvider[];
  currentProvider: RuntimeKey;
  currentRuntimeId: string;
  currentModel: string;
  /** "default" (no override) or a real effort value. */
  currentEffort: Effort;
  onChangeModel: (
    provider: InputBarProvider,
    modelId: string,
  ) => void | Promise<void>;
  onChangeEffort: (effort: Effort) => void | Promise<void>;
  onClose: () => void;
  className?: string;
  popoverScope?: "tray";
  stopEscapePropagation?: boolean;
  motionState?: "open" | "closed";
  style?: CSSProperties;
}

export const ModelPickerPopover = forwardRef<HTMLDivElement, Props>(
  function ModelPickerPopover(
    {
      providers,
      currentProvider,
      currentRuntimeId,
      currentModel,
      currentEffort,
      onChangeModel,
      onChangeEffort,
      onClose,
      className,
      popoverScope,
      stopEscapePropagation = false,
      motionState = "open",
      style,
    },
    forwardedRef,
  ) {
    const t = useT(inputBarPickersMessages);
    const summonedInputMenuStyle = useSummonedInputMenuStyle();
    const [tab, setTab] = useState(currentRuntimeId);
    const [search, setSearch] = useState("");
    // Only mark a model selected after persistence succeeds. Lock effort
    // edits while saving so they cannot overwrite the new runtime config.
    const [pendingProvider, setPendingProvider] = useState(currentRuntimeId);
    const [pendingModel, setPendingModel] = useState(currentModel);
    const [savingConfig, setSavingConfig] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const savingConfigRef = useRef(false);
    const availableModels = useCompanionStore((state) => state.availableModels);
    const ref = useRef<HTMLDivElement>(null);
    const setRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        ref.current = node;
        setForwardedRef(forwardedRef, node);
      },
      [forwardedRef],
    );

    // Keep local pending state in sync with props once the parent catches up.
    useEffect(() => {
      setPendingProvider(currentRuntimeId);
      setPendingModel(currentModel);
    }, [currentRuntimeId, currentModel]);

    useEffect(() => {
      const onDoc = (e: MouseEvent) => {
        if (e.button !== 0) return;
        if (ref.current && !ref.current.contains(e.target as Node)) onClose();
      };
      const onEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          if (popoverScope === "tray" || stopEscapePropagation)
            e.stopPropagation();
          onClose();
        }
      };
      document.addEventListener("click", onDoc);
      document.addEventListener("keydown", onEsc);
      return () => {
        document.removeEventListener("click", onDoc);
        document.removeEventListener("keydown", onEsc);
      };
    }, [onClose, popoverScope, stopEscapePropagation]);

    const activeProvider = useMemo(
      () =>
        providers.find((p) => providerRuntimeId(p) === tab) ??
        providers.find((p) => p.type === currentProvider),
      [currentProvider, providers, tab],
    );

    // Personal OpenCode lists one model per plan it bills on, so the rows
    // get plan headers. Bundled OpenCode and the other CLIs do not.
    const planGrouped = activeProvider
      ? usesOpencodePlanGroups(
          activeProvider.type,
          providers,
          providerRuntimeId(activeProvider),
        )
      : false;

    const models = useMemo(
      () =>
        activeProvider
          ? getModelsForRuntime(
              activeProvider.type,
              availableModels,
              providerRuntimeId(activeProvider),
              planGrouped,
            )
          : [],
      [activeProvider, availableModels, planGrouped],
    );
    const showSearch = activeProvider?.type === "opencode_cli";
    // Effort controls follow the saved model, never an in-flight selection.
    const effortConfig = getModelEffortConfig(
      tab === pendingProvider ? pendingModel : undefined,
    );
    const showEffort = effortConfig.values.length > 0;
    const providerUnavailable =
      activeProvider?.detected === false || activeProvider?.loggedIn === false;
    const effortDisabled = savingConfig || providerUnavailable;
    const efforts: readonly Effort[] = effortConfig.values;
    const selectedEffort =
      currentEffort !== "default" && efforts.includes(currentEffort)
        ? currentEffort
        : effortConfig.default;

    const selectModel = async (provider: InputBarProvider, model: string) => {
      if (
        savingConfigRef.current ||
        (providerRuntimeId(provider) === pendingProvider &&
          model === pendingModel) ||
        provider.detected === false ||
        provider.loggedIn === false
      )
        return;
      savingConfigRef.current = true;
      setSavingConfig(true);
      setSaveError(null);
      try {
        await onChangeModel(provider, model);
        setPendingProvider(providerRuntimeId(provider));
        setPendingModel(model);
      } catch {
        setSaveError(t("changeModelFailed"));
      } finally {
        savingConfigRef.current = false;
        setSavingConfig(false);
      }
    };

    const selectEffort = async (effort: Effort) => {
      if (
        savingConfigRef.current ||
        effortDisabled ||
        !efforts.includes(effort)
      )
        return;
      savingConfigRef.current = true;
      setSavingConfig(true);
      setSaveError(null);
      try {
        await onChangeEffort(effort);
      } catch {
        setSaveError(t("changeEffortFailed"));
      } finally {
        savingConfigRef.current = false;
        setSavingConfig(false);
      }
    };

    return (
      <div
        ref={setRootRef}
        className={cn(
          surfaceRoleClassName("menu"),
          menuSurfaceVariants(),
          "flex h-80 max-h-[calc(100vh-24px)] w-64 max-w-[calc(100vw-24px)] flex-col overflow-hidden",
          className,
        )}
        style={{ ...style, ...summonedInputMenuStyle }}
        data-testid="model-picker-popover"
        data-tray-popover={popoverScope === "tray" || undefined}
        data-interactive="true"
        data-state={motionState}
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
      >
        <div className="shrink-0 border-b border-glass-stroke-subtle pb-appkit-xs">
          <div
            className={cn(
              "grid gap-appkit-2xs",
              providerGridClass(providers.length),
            )}
            data-testid="model-picker-provider-grid"
          >
            {providers.map((p) => {
              const runtimeId = providerRuntimeId(p);
              const isActive = tab === runtimeId;
              const label = PROVIDER_LABELS[p.type];
              const runtimeLabel = p.managed
                ? t("bundled", { runtime: label })
                : label;
              // Treat `detected: undefined` as detected — keeps existing
              // call sites that don't set the field working.
              const notDetected = p.detected === false;
              // Only disable when auth is explicitly false (not null/undefined).
              // Inconclusive live CLI probes come back as null and should not
              // disable an otherwise detected runtime.
              const notLoggedIn = p.loggedIn === false;
              const unavailable = notDetected || notLoggedIn;
              const disabled = unavailable || savingConfig;
              const hint = notDetected
                ? t("cliNotDetected", { runtime: label })
                : p.loginCommand
                  ? t("loginRequiredCommand", { command: p.loginCommand })
                  : t("loginRequired");
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  key={runtimeId}
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    setTab(runtimeId);
                    setSearch("");
                    setSaveError(null);
                  }}
                  disabled={disabled}
                  title={
                    savingConfig
                      ? t("savingSettings")
                      : disabled
                        ? hint
                        : runtimeLabel
                  }
                  aria-pressed={isActive && !unavailable}
                  className={cn(
                    menuItemVariants(),
                    MODEL_PICKER_CONTROL_HEIGHT,
                    MODEL_PICKER_CONTROL_SHAPE,
                    "min-w-0 justify-center border py-0 text-center font-medium aria-[pressed=true]:bg-[var(--color-surface-selected)] aria-[pressed=true]:text-primary focus-visible:ring-1 focus-visible:ring-accent",
                    isActive && !unavailable
                      ? "border-glass-stroke-light bg-[var(--color-surface-selected)] text-primary"
                      : "border-transparent text-secondary hover:bg-[var(--color-interactive-hover)] hover:text-primary",
                    unavailable
                      ? "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-muted"
                      : "disabled:opacity-100",
                  )}
                  aria-label={runtimeLabel}
                  data-testid={`provider-tab-${runtimeId.replace(/[^a-z0-9_-]/gi, "-")}`}
                  data-selected={isActive && !unavailable ? "true" : undefined}
                  data-logged-in={p.loggedIn ? "true" : "false"}
                >
                  <span
                    className="relative flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <CliLogo
                      cli={
                        p.type === "claude_cli"
                          ? "claude"
                          : p.type === "codex_cli"
                            ? "codex"
                            : "opencode"
                      }
                      className="h-4 w-4"
                    />
                    {p.managed && (
                      <Icons.Package className="absolute -right-3 -bottom-1 h-2.5 w-2.5 text-secondary" />
                    )}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>

        {showSearch && (
          <div className="shrink-0 py-appkit-xs">
            <TextInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("searchModelsPlaceholder")}
              aria-label={t("searchModels")}
              shape="field"
              className={cn(
                MODEL_PICKER_CONTROL_HEIGHT,
                MODEL_PICKER_CONTROL_SHAPE,
                "py-0",
              )}
              data-testid="model-picker-search"
            />
          </div>
        )}

        <ModelPickerList
          key={tab}
          models={models}
          search={search}
          selectedModel={tab === pendingProvider ? pendingModel : undefined}
          planGrouped={planGrouped}
          saving={savingConfig}
          unavailable={providerUnavailable}
          onSelect={(model) => {
            if (activeProvider) void selectModel(activeProvider, model);
          }}
        />

        <FormError
          message={saveError ?? undefined}
          data-testid="model-picker-error"
        />

        <div
          className="h-14 shrink-0 border-t border-glass-stroke-subtle px-appkit-xs pt-1"
          data-testid="model-picker-thinking-footer"
        >
          <div className="flex items-center justify-between px-appkit-2xs text-appkit-caption">
            <span className="min-w-0 truncate text-muted">{t("thinking")}</span>
            <span className="shrink-0 text-primary" aria-live="polite">
              {showEffort && selectedEffort
                ? t(EFFORT_LABEL_KEYS[selectedEffort])
                : t("unavailable")}
            </span>
          </div>
          <div
            className="mt-1 flex items-center gap-appkit-2xs"
            role="group"
            aria-label={t("thinkingLevel")}
            aria-disabled={!showEffort || effortDisabled}
            data-testid="model-picker-thinking-ticks"
          >
            {efforts.map((effort) => (
              <Button
                key={effort}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void selectEffort(effort)}
                disabled={effortDisabled}
                className={cn(
                  "h-7 min-w-0 flex-1 justify-center px-0 py-0 aria-[pressed=true]:bg-transparent aria-[pressed=true]:text-primary focus-visible:ring-1 focus-visible:ring-accent",
                  !providerUnavailable && "disabled:opacity-100",
                )}
                aria-pressed={selectedEffort === effort}
                aria-label={t("effortThinking", {
                  effort: t(EFFORT_LABEL_KEYS[effort]),
                })}
                title={t(EFFORT_LABEL_KEYS[effort])}
                data-selected={selectedEffort === effort ? "true" : undefined}
                data-testid={`think-effort-${effort}`}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "w-1 rounded-full bg-current transition-[height]",
                    selectedEffort === effort
                      ? "h-4 text-primary"
                      : "h-2.5 text-muted",
                  )}
                />
              </Button>
            ))}
            {!showEffort &&
              Array.from({ length: 5 }, (_, index) => (
                <span
                  key={index}
                  className="flex h-7 flex-1 items-center justify-center"
                  aria-hidden="true"
                >
                  <span className="h-2.5 w-1 rounded-full bg-current text-muted opacity-50" />
                </span>
              ))}
          </div>
        </div>
      </div>
    );
  },
);
